import { useEffect, useState } from "react";
import api from "../lib/api";
import { CurrencyDollar, ChartLineUp, Lightning, ArrowsClockwise, Receipt } from "@phosphor-icons/react";

const STATUS_COLOR = {
  completed: "text-neon-lime border-[#F2DC8C]",
  initiated: "text-neon-cyan border-[#BEE7F0]",
  expired: "text-muted-foreground border-white/20",
  failed: "text-neon-pink border-[#D4AF37]",
};

export default function PaymentsAdmin() {
  const [stats, setStats] = useState(null);
  const [txns, setTxns] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/payments/stats"),
        api.get(`/admin/payments${status ? `?status=${status}` : ""}`),
      ]);
      setStats(s.data);
      setTxns(t.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-2xl flex items-center gap-2">
            <Receipt size={22} weight="duotone" className="text-neon-cyan" /> Payments ·
            {stats?.live ? <span className="text-neon-lime text-sm">LIVE</span> : <span className="text-muted-foreground text-sm">TEST</span>}
          </div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Revenue ledger across all boost purchases</div>
        </div>
        <button data-testid="payments-refresh" onClick={load} className="btn-ghost text-xs"><ArrowsClockwise size={12}/> Refresh</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="card-sharp p-4 glow-lime" data-testid="payments-stat-revenue">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total revenue</div>
            <div className="font-display text-3xl text-neon-lime mt-1 flex items-baseline gap-1">
              <CurrencyDollar size={18} weight="bold"/>{stats.total_revenue.toFixed(2)}
            </div>
          </div>
          <div className="card-sharp p-4" data-testid="payments-stat-completed">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Completed</div>
            <div className="font-display text-3xl text-neon-pink mt-1">{stats.completed}</div>
          </div>
          <div className="card-sharp p-4" data-testid="payments-stat-initiated">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pending</div>
            <div className="font-display text-3xl text-neon-cyan mt-1">{stats.initiated}</div>
          </div>
          <div className="card-sharp p-4" data-testid="payments-stat-failed">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Failed / expired</div>
            <div className="font-display text-3xl text-muted-foreground mt-1">{stats.failed_or_expired}</div>
          </div>
        </div>
      )}

      {stats?.by_tier?.length > 0 && (
        <div className="card-sharp p-5">
          <div className="text-xs uppercase tracking-widest text-neon-pink font-display flex items-center gap-1 mb-3"><ChartLineUp size={12} weight="fill"/> Revenue by Tier</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {stats.by_tier.map(t => (
              <div key={t.tier} data-testid={`payments-by-tier-${t.tier}`} className="border border-white/10 p-3">
                <div className="text-sm uppercase tracking-widest font-display">{t.tier.replace("_", " ")}</div>
                <div className="font-display text-2xl text-neon-lime">${t.revenue.toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.count} purchase{t.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-sharp p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-neon-cyan font-display flex items-center gap-1">
            <Lightning size={12} weight="fill"/> Recent Transactions
          </div>
          <select data-testid="payments-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="input-sharp w-auto text-[10px] uppercase tracking-widest">
            <option value="">All status</option>
            <option value="completed">Completed</option>
            <option value="initiated">Pending</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {!loading && txns.length === 0 && <div className="text-xs text-muted-foreground">No transactions yet.</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/10">
                <th className="text-left py-2">When</th>
                <th className="text-left">User</th>
                <th className="text-left">Job</th>
                <th className="text-left">Tier</th>
                <th className="text-right">Amount</th>
                <th className="text-left pl-3">Status</th>
                <th className="text-left">Session</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.session_id} data-testid={`payments-row-${t.session_id}`} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="text-muted-foreground">{t.user_email}</td>
                  <td className="truncate max-w-[180px]">{t.gig_title || t.gig_id || "—"}</td>
                  <td className="text-muted-foreground">{t.tier?.replace("tier_", "")}</td>
                  <td className="text-right text-neon-lime">${Number(t.amount || 0).toFixed(2)}</td>
                  <td className="pl-3"><span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_COLOR[t.status] || "border-white/10 text-muted-foreground"}`}>{t.status}</span></td>
                  <td className="font-mono text-[10px] text-muted-foreground truncate max-w-[160px]">{t.session_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
